import React, { useState } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faShare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useGitHubInstallationShare } from '../hooks/useGitHubInstallationShare';
import { useGitHubInstallationShares, useDeleteGitHubInstallationShare } from '../hooks/useGitHubInstallationShares';

interface GitHubInstallation {
    connectionId: string;
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization';
    repositorySelection: 'all' | 'selected';
    isActive: boolean;
    name: string;
    description?: string;
}

interface Tenant {
    id: string;
    name: string;
    verified: boolean;
}

interface GitHubAppInstallationShareProps {
    installation: GitHubInstallation;
    tenants: Tenant[];
    onCancel: () => void;
    onSuccess?: () => void;
}

const GitHubAppInstallationShare: React.FC<GitHubAppInstallationShareProps> = ({
    installation,
    tenants,
    onCancel,
    onSuccess
}) => {
    const [selectedTenant, setSelectedTenant] = useState('');
    const queryClient = useQueryClient();
    const shareInstallation = useGitHubInstallationShare();
    const { data: sharesData, isLoading: sharesLoading } = useGitHubInstallationShares(installation.connectionId);
    const deleteShare = useDeleteGitHubInstallationShare();

    const handleShare = async () => {
        if (!selectedTenant) return;

        try {
            await shareInstallation.mutateAsync({
                installationId: installation.installationId,
                targetTenantId: selectedTenant
            });
            queryClient.invalidateQueries({ queryKey: ['githubInstallationShares', installation.connectionId] });
            onSuccess?.();
            onCancel();
        } catch (error) {
            console.error('Failed to share installation:', error);
        }
    };

    const selectedTenantData = tenants.find(
        tenant => tenant.id === selectedTenant
    );

    const handleDeleteShare = async (tenantId: string) => {
        try {
            await deleteShare.mutateAsync({
                connectionId: installation.connectionId,
                tenantId: tenantId
            });
        } catch (error) {
            console.error('Failed to delete share:', error);
        }
    };

    const getTenantName = (tenantId: string) => {
        const tenant = tenants.find(t => t.id === tenantId);
        return tenant?.name || tenantId;
    };

    const shares = sharesData?.shares || [];

    const verifiedTenants = tenants.filter(tenant => tenant.verified);

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faShare} />
                Share GitHub App Installation
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Share this GitHub App installation with another tenant to enable access.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontAwesomeIcon icon={faRobot} />
                    <Box>
                        <Typography variant="body2">
                            <strong>Installation:</strong> {installation.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {installation.accountLogin} ({installation.accountType})
                        </Typography>
                    </Box>
                </Box>
            </Alert>

            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Target Tenant</InputLabel>
                <Select
                    value={selectedTenant}
                    onChange={(e) => setSelectedTenant(e.target.value)}
                    label="Target Tenant"
                >
                    {verifiedTenants.map((tenant) => (
                        <MenuItem key={tenant.id} value={tenant.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                    {tenant.name}
                                </Typography>
                                <Chip size="small" label="Verified" color="success" />
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {shareInstallation.error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {shareInstallation.error.message}
                </Alert>
            )}

            {selectedTenantData && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        <strong>Sharing:</strong> {installation.name} with {selectedTenantData.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        The target tenant will gain access to this GitHub App installation.
                    </Typography>
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={shareInstallation.isPending}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleShare}
                    disabled={!selectedTenant || shareInstallation.isPending}
                    startIcon={<FontAwesomeIcon icon={faShare} />}
                >
                    {shareInstallation.isPending ? 'Sharing...' : 'Share Installation'}
                </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
                Current Shares
            </Typography>

            {sharesLoading ? (
                <Typography variant="body2" color="text.secondary">
                    Loading shares...
                </Typography>
            ) : shares.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    This installation has not been shared with any tenants yet.
                </Typography>
            ) : (
                <List>
                    {shares.map((share) => (
                        <ListItem key={share.tenantId} divider>
                            <ListItemText
                                primary={getTenantName(share.tenantId)}
                                secondary={`Shared on ${new Date(share.sharedAt).toLocaleDateString()} by ${share.sharedBy}`}
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    onClick={() => handleDeleteShare(share.tenantId)}
                                    disabled={deleteShare.isPending}
                                    color="error"
                                    title="Remove share"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default GitHubAppInstallationShare;